import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../layouts/layout";
import Loader from "../components/loader";
import Spinner from "../components/spinner";
import WA_BOT from "../api/services/WA_BOT";
import styles from "../styles/_screens/whatsappBot.module.scss";

// הגדרות מזכירת ה-AI — the owner configures an employee, not a prompt.
//
// Every control here maps to a field in Business.waBot.aiSettings, which the
// server compiles into the agent's Hebrew directives on save
// (Backend: src/services/waBot/aiSettingsCompiler.js) and rides on
// ai.instructions. Field names below MUST match that compiler exactly, and the
// sections mirror the mobile app's AIRecepSettingsScreen so the two surfaces
// configure one and the same receptionist.
//
// What this screen deliberately does NOT send: `directions` (the address and
// the Waze link are sourced automatically from the business) and
// `registrationInfo` (identification + registration are automatic now:
// phone → OTP → the new-client policy). Both are cleared on save so the
// leftovers of the old dashboard form stop steering the bot.

const WA_BOT_PATH = "/whatsapp-bot";

const TONES = [
  { v: "professional", label: "מקצועי" },
  { v: "friendly", label: "חברותי" },
  { v: "concise", label: "קצר ולעניין" },
  { v: "premium", label: "יוקרתי" },
];
const LENGTHS = [
  { v: "short", label: "קצר" },
  { v: "medium", label: "בינוני" },
  { v: "detailed", label: "מפורט" },
];
const GOALS = [
  { v: "book", label: "לקבוע תורים" },
  { v: "sell", label: "למכור" },
  { v: "answer", label: "לענות על שאלות" },
  { v: "lead", label: "להשאיר ליד" },
  { v: "handoff", label: "להעביר לנציג" },
];
const APPT_ROWS = [
  { k: "book", label: "קביעת תורים" },
  { k: "cancel", label: "ביטול תורים" },
  { k: "reschedule", label: "שינוי תורים" },
  { k: "suggestAlternatives", label: "הצעת שעות חלופיות" },
  { k: "listFreeSlots", label: "רשימת שעות פנויות" },
  { k: "lastMinute", label: "תורים לרגע האחרון" },
];
// How a caller becomes a client. Shown as the steps the system already takes,
// because none of it is optional or configurable.
const IDENTITY_STEPS = [
  "הלקוח מזוהה לפי מספר הוואטסאפ שממנו כתב.",
  "אם המספר לא נקרא — היא מבקשת אותו ושולחת קוד אימות (OTP) דרך המערכת שלנו.",
  "אם הוא לא רשום אצלכם — נבדקת מדיניות הלקוחות החדשים שהגדרתם במסך הבוט.",
  "אם ההרשמה פתוחה — הוא נרשם בשם ובטלפון (ומייל, אם מסר) תוך כדי קביעת התור.",
];
// Everything the receptionist knows without being told — read live from the
// business's own settings, so there is nothing here to switch on or off.
const AUTO_SOURCES = [
  { label: "שירותים, מחירים ומשך טיפול", from: "מרשימת השירותים שלכם, בזמן אמת" },
  { label: "שעות פעילות", from: "משעות הפעילות שהגדרתם בעסק" },
  { label: "כתובת ואיך מגיעים", from: "מכתובת העסק, כולל קישור ניווט ב-Waze" },
  { label: "האם יש תור היום", from: "מהיומן — ואם אין, היא מציעה מיד את המועד הפנוי הקרוב" },
  { label: "מדיניות ביטולים", from: "מהגדרות יומן ותורים" },
];
const HANDOFF_ROWS = [
  { k: "asked_for_human", label: "הלקוח מבקש אדם" },
  { k: "frustrated", label: "הלקוח מתעצבן" },
  { k: "repeated", label: "אותה שאלה חוזרת" },
  { k: "stuck", label: "תקוע באמצע פעולה" },
  { k: "technical", label: "תקלה טכנית" },
];

// Defaults: everything a receptionist should do is ON; the owner switches
// things OFF. Matches the compiler, which only emits chosen deviations.
const DEFAULTS = {
  personality: { botName: "", tone: "friendly", emojis: true, answerLength: "medium" },
  appointments: {
    book: true,
    cancel: true,
    reschedule: true,
    suggestAlternatives: true,
    listFreeSlots: true,
    lastMinute: true,
  },
  intake: { customQuestion: "" },
  qa: [],
  handoff: {
    asked_for_human: true,
    frustrated: true,
    repeated: true,
    stuck: true,
    technical: true,
    afterMessages: 0,
  },
  sales: { recommend: false, upsell: false },
  goal: "book",
  knowledgeText: "",
};

const merge = (saved) => ({
  ...DEFAULTS,
  ...(saved || {}),
  personality: { ...DEFAULTS.personality, ...((saved && saved.personality) || {}) },
  appointments: { ...DEFAULTS.appointments, ...((saved && saved.appointments) || {}) },
  intake: { ...DEFAULTS.intake, ...((saved && saved.intake) || {}) },
  qa: Array.isArray(saved && saved.qa) ? saved.qa : [],
  handoff: { ...DEFAULTS.handoff, ...((saved && saved.handoff) || {}) },
  sales: { ...DEFAULTS.sales, ...((saved && saved.sales) || {}) },
  // The two scalars need the same defence as the sections above: a stored null
  // (an unset field in Mongo, or an older writer) would otherwise ride straight
  // back to the compiler as `null` — and leave the goal chips with nothing
  // selected while the owner is looking at them.
  goal:
    typeof (saved && saved.goal) === "string" && saved.goal
      ? saved.goal
      : DEFAULTS.goal,
  knowledgeText:
    typeof (saved && saved.knowledgeText) === "string"
      ? saved.knowledgeText
      : DEFAULTS.knowledgeText,
});

/**
 * Free text the owner wrote in the OLD flat AI card on the bot screen, which
 * this screen replaces. Those fields live on the WhatsApp platform, not in our
 * DB, so without this they would keep steering the bot with no way to see or
 * edit them. Folded into "מאגר ידע חופשי" — and ONLY for a business that never
 * saved this form, so it can never overwrite real input. `directions` is
 * skipped: the address and the Waze link are sourced automatically now.
 */
const legacyKnowledge = (data) => {
  const ai = (data && data.bot && data.bot.ai) || {};
  return [ai.registrationInfo, ai.instructions]
    .map((x) => (x || "").trim())
    .filter(Boolean)
    .join("\n\n");
};

// The platform still keeps a numeric legacy tone next to the compiled block.
// Mirror the structured choice into it so the two never contradict each other.
const LEGACY_TONE = { friendly: 1, concise: 3, professional: 4, premium: 4 };

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);

const sx = {
  backLink: {
    alignSelf: "flex-start",
    padding: 0,
    border: "none",
    background: "transparent",
    color: "#64748b",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  cardHead: { display: "flex", alignItems: "center", gap: 10, marginBottom: 4 },
  cardIcon: { fontSize: 22, lineHeight: 1 },
  hint: { fontSize: 13, lineHeight: 1.7, color: "#64748b", margin: "6px 0 0" },
  rowsWrap: { marginTop: 8 },
  toggleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 0",
    borderBottom: "1px solid #f1f5f9",
  },
  toggleLabel: { fontSize: 14, fontWeight: 600, color: "#1e293b" },
  note: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    background: "#f0fdf4",
    border: "1px solid #dcfce7",
    borderRadius: 10,
    padding: "10px 12px",
    marginTop: 8,
    fontSize: 13,
    lineHeight: 1.7,
    color: "#166534",
  },
  stepRow: { display: "flex", alignItems: "flex-start", gap: 10, marginTop: 10 },
  stepNum: {
    flexShrink: 0,
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: "#f0fdf4",
    color: "#166534",
    fontSize: 12,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  stepText: { fontSize: 13.5, lineHeight: 1.7, color: "#334155" },
  qaRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "10px 12px",
    marginTop: 8,
  },
  qaQ: { fontSize: 14, fontWeight: 700, color: "#1e293b" },
  qaA: { fontSize: 13, lineHeight: 1.6, color: "#64748b", marginTop: 2 },
  qaDelete: {
    flexShrink: 0,
    border: "none",
    background: "transparent",
    color: "#dc2626",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  smallInput: { width: 120 },
  statsCard: {
    background: "#0b1b36",
    borderRadius: 16,
    padding: "18px 24px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  statsTitle: { fontSize: 13, fontWeight: 700, color: "#94a3b8" },
  statsRow: { display: "flex", flexWrap: "wrap", gap: 12 },
  statBox: { flex: "1 1 120px", textAlign: "center" },
  statNum: { fontSize: 24, fontWeight: 800, color: "#fff", lineHeight: 1.2 },
  statLabel: { fontSize: 12, color: "#94a3b8", marginTop: 2 },
};

const Card = ({ icon, title, subtitle, hint, children }) => (
  <div className={styles.card}>
    <div style={sx.cardHead}>
      <span style={sx.cardIcon}>{icon}</span>
      <div>
        <span className={styles.cardTitle}>{title}</span>
        {!!subtitle && <p className={styles.body}>{subtitle}</p>}
      </div>
    </div>
    {!!hint && <p style={sx.hint}>{hint}</p>}
    {children}
  </div>
);

const ToggleRow = ({ label, value, onChange }) => (
  <div style={sx.toggleRow}>
    <span style={sx.toggleLabel}>{label}</span>
    <label className={styles.switch}>
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
      <span className={styles.slider} />
    </label>
  </div>
);

const Chips = ({ options, value, onChange }) => (
  <div className={styles.toneRow}>
    {options.map((o) => (
      <button
        type="button"
        key={o.v}
        onClick={() => onChange(o.v)}
        className={`${styles.toneChip} ${value === o.v ? styles.toneChipOn : ""}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const AiReceptionistSettings = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(DEFAULTS);
  const [stats, setStats] = useState(null);
  // The platform's own AI switch, preserved as-is: this screen configures how
  // the receptionist behaves, it must not turn her on or off behind the back of
  // the bot screen's toggle.
  const [aiEnabled, setAiEnabled] = useState(true);
  const [qaQ, setQaQ] = useState("");
  const [qaA, setQaA] = useState("");

  const patch = useCallback((section, kv) => {
    setForm((f) => ({ ...f, [section]: { ...f[section], ...kv } }));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [statusRes, statsRes] = await Promise.all([
          WA_BOT.get_status(),
          // Not deployed everywhere yet — a missing endpoint must not take the
          // whole screen down, it just hides the performance card.
          WA_BOT.stats(30).catch(() => null),
        ]);
        const data = (statusRes && statusRes.data) || {};
        setStatus(data);
        if (data.bot && data.bot.ai && typeof data.bot.ai.aiEnabled === "boolean") {
          setAiEnabled(data.bot.ai.aiEnabled);
        }
        if (data.aiSettings) {
          setForm(merge(data.aiSettings));
        } else {
          // First visit — carry the old flat form's free text over so nothing
          // the owner wrote is lost when the legacy fields stop being sent.
          const legacy = legacyKnowledge(data);
          if (legacy) setForm((f) => ({ ...f, knowledgeText: legacy }));
        }
        setStats((statsRes && statsRes.data) || null);
      } catch (e) {
        toast.error("שגיאה בטעינת הגדרות המזכירה");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const res = await WA_BOT.save_settings({
        // The structured form — stored verbatim and compiled server-side.
        aiSettings: form,
        // §6.5 — the ai block always travels whole, or the platform drops the
        // fields we leave out of the knowledge block. `instructions` is sent
        // EMPTY on purpose: the owner's free text already reaches
        // ai.instructions through the compiler (## מידע חופשי על העסק), which
        // the server prepends before any free text — sending it here as well
        // would paste the same knowledge into the prompt twice.
        ai: {
          aiEnabled,
          tone: LEGACY_TONE[form.personality.tone] || 2,
          directions: "", // removed — address + Waze link are auto-sourced
          registrationInfo: "", // removed — identification/registration is automatic
          instructions: "",
          handoffConditionText: "", // superseded by the structured handoff section
        },
      });
      if (res?.data?.status === 200) {
        toast.success("ההגדרות נשמרו — המזכירה עודכנה");
      } else {
        toast.error(res?.data?.message || "שגיאה בשמירת ההגדרות");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "שגיאה בשמירת ההגדרות");
    } finally {
      setSaving(false);
    }
  }, [form, aiEnabled]);

  const addQa = () => {
    const q = qaQ.trim();
    const a = qaA.trim();
    if (!q || !a) return;
    setForm((f) => ({ ...f, qa: [...(f.qa || []), { q, a }] }));
    setQaQ("");
    setQaA("");
  };

  if (loading) {
    return (
      <Layout>
        <div className={styles.container}>
          <div className={styles.wrapper}>
            <div className={styles.content}>
              <Loader />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // The settings endpoint needs both the feature and a linked number; showing
  // the form to a business that has neither would only produce a 403/409 on
  // save.
  const usable = !!(status && status.addOnActive && status.connected);

  // Performance card — every field is optional (older backends answer with
  // fewer of them), so build the tiles from what actually came back.
  const statTiles = stats
    ? [
        { label: "שיחות", value: num(stats.conversations) },
        { label: "תורים נקבעו", value: num(stats.booked) },
        { label: "בוטלו", value: num(stats.cancelled) },
        { label: "לידים", value: num(stats.leads) },
      ].filter((t) => t.value !== null)
    : [];

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <div className={styles.content}>
            <button type="button" style={sx.backLink} onClick={() => navigate(WA_BOT_PATH)}>
              ← חזרה לבוט הוואטסאפ
            </button>
            <h1 className={styles.pageTitle}>הגדרות מזכירת AI</h1>

            {!usable && (
              <div className={styles.card}>
                <span className={styles.cardTitle}>המזכירה עדיין לא פעילה</span>
                <p className={styles.body}>
                  כדי להגדיר את המזכירה צריך שהתוסף יהיה פעיל ושמספר הוואטסאפ של
                  העסק יחובר. אפשר לשמור הגדרות רק אחרי החיבור.
                </p>
                <button
                  type="button"
                  className={styles.primaryBtn}
                  onClick={() => navigate(WA_BOT_PATH)}
                >
                  מעבר למסך הבוט
                </button>
              </div>
            )}

            {statTiles.length > 0 && (
              <div style={sx.statsCard}>
                <span style={sx.statsTitle}>
                  {`${num(stats.days) || 30} הימים האחרונים`}
                </span>
                <div style={sx.statsRow}>
                  {statTiles.map((t) => (
                    <div key={t.label} style={sx.statBox}>
                      <div style={sx.statNum}>{t.value}</div>
                      <div style={sx.statLabel}>{t.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 🧠 personality */}
            <Card
              icon="🧠"
              title="אישיות המזכירה"
              subtitle="איך היא מציגה את עצמה ואיך היא נשמעת"
              hint="השם נאמר בפתיחת כל שיחה. שפה אינה הגדרה — היא תמיד עונה בשפה שבה הלקוח כתב."
            >
              <label className={styles.fieldLabel}>שם המזכירה</label>
              <input
                className={styles.input}
                type="text"
                value={form.personality.botName}
                onChange={(e) => patch("personality", { botName: e.target.value })}
                placeholder='למשל: "נועה"'
              />
              <label className={styles.fieldLabel}>טון דיבור</label>
              <Chips
                options={TONES}
                value={form.personality.tone}
                onChange={(v) => patch("personality", { tone: v })}
              />
              <div style={sx.rowsWrap}>
                <ToggleRow
                  label="שימוש באימוג'ים"
                  value={form.personality.emojis}
                  onChange={(v) => patch("personality", { emojis: v })}
                />
              </div>
              <label className={styles.fieldLabel}>אורך תשובות</label>
              <Chips
                options={LENGTHS}
                value={form.personality.answerLength}
                onChange={(v) => patch("personality", { answerLength: v })}
              />
              <div style={sx.note}>
                שפה — אוטומטי. היא עונה בשפה שבה הלקוח כתב, ומתחלפת איתו אם הוא
                מחליף.
              </div>
            </Card>

            {/* 📅 appointments */}
            <Card
              icon="📅"
              title="ניהול תורים"
              subtitle="מה מותר לה לעשות ביומן"
              hint='מתג שכבוי הופך לאיסור מפורש: היא תסביר בנימוס שהפעולה נעשית מול נציג ותציע להעביר אליכם. "תורים לרגע האחרון" = פחות משעתיים מראש.'
            >
              <div style={sx.rowsWrap}>
                {APPT_ROWS.map((r) => (
                  <ToggleRow
                    key={r.k}
                    label={r.label}
                    value={form.appointments[r.k]}
                    onChange={(v) => patch("appointments", { [r.k]: v })}
                  />
                ))}
              </div>
            </Card>

            {/* 🪪 identity & registration — described, not configured */}
            <Card
              icon="🪪"
              title="זיהוי הלקוח והרשמה"
              subtitle="קורה אוטומטית — אין מה להגדיר"
              hint="בלי אימות היא לא תקבע תור על מספר שהוקלד ידנית. לקוח שאינו רשום נרשם תוך כדי קביעת התור, בכפוף למדיניות הלקוחות החדשים שבמסך הבוט."
            >
              {IDENTITY_STEPS.map((step, i) => (
                <div key={step} style={sx.stepRow}>
                  <span style={sx.stepNum}>{i + 1}</span>
                  <span style={sx.stepText}>{step}</span>
                </div>
              ))}
              <label className={styles.fieldLabel}>שאלה נוספת משלכם (לא חובה)</label>
              <input
                className={styles.input}
                type="text"
                value={form.intake.customQuestion}
                onChange={(e) => patch("intake", { customQuestion: e.target.value })}
                placeholder='למשל: "מאיפה שמעת עלינו?"'
              />
            </Card>

            {/* ❓ what she already knows — sourced, not toggled */}
            <Card
              icon="❓"
              title="מה המזכירה כבר יודעת"
              subtitle="נשלף אוטומטית מהמערכת — תמיד מעודכן"
            >
              {AUTO_SOURCES.map((r) => (
                <div key={r.label} style={sx.note}>
                  <span>
                    <b>{r.label}</b>
                    {` — ${r.from}`}
                  </span>
                </div>
              ))}
              <p style={sx.hint}>
                כל שאלה אחרת שאינה כאן — הוסיפו אותה במאגר השאלות והתשובות שלמטה.
                בלי תשובה כתובה היא לא תנחש: תאמר בכנות שאין לה את המידע ותציע
                להעביר אליכם.
              </p>
            </Card>

            {/* 📄 Q&A pairs */}
            <Card
              icon="📄"
              title="מאגר ידע — שאלות ותשובות"
              subtitle="תשובות מדויקות במילים שלכם"
              hint="כשלקוח שואל משהו דומה היא מצטטת את התשובה שכתבתם ולא מנסחת מחדש. מתאים לשאלות שחוזרות — חניה, נגישות, מעלית, הגעה עם ילדים."
            >
              {(form.qa || []).map((row, i) => (
                <div key={`${row.q}-${i}`} style={sx.qaRow}>
                  <div style={{ flex: 1 }}>
                    <div style={sx.qaQ}>{row.q}</div>
                    <div style={sx.qaA}>{row.a}</div>
                  </div>
                  <button
                    type="button"
                    style={sx.qaDelete}
                    onClick={() =>
                      setForm((f) => ({ ...f, qa: f.qa.filter((_, j) => j !== i) }))
                    }
                  >
                    מחיקה
                  </button>
                </div>
              ))}
              <label className={styles.fieldLabel}>שאלה</label>
              <input
                className={styles.input}
                type="text"
                value={qaQ}
                onChange={(e) => setQaQ(e.target.value)}
                placeholder='למשל: "כמה עולה טיפול?"'
              />
              <label className={styles.fieldLabel}>תשובה</label>
              <textarea
                className={styles.input}
                rows={2}
                value={qaA}
                onChange={(e) => setQaA(e.target.value)}
                placeholder='למשל: "220 ₪."'
              />
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={addQa}
                disabled={!qaQ.trim() || !qaA.trim()}
              >
                הוספה למאגר
              </button>
            </Card>

            {/* 🚨 handoff */}
            <Card
              icon="🚨"
              title="מתי להעביר לנציג"
              subtitle="המקרים שבהם היא עוצרת ומעבירה אליכם"
              hint="ההעברה מיידית — היא לא תנסה לפתור קודם."
            >
              <div style={sx.rowsWrap}>
                {HANDOFF_ROWS.map((r) => (
                  <ToggleRow
                    key={r.k}
                    label={r.label}
                    value={form.handoff[r.k]}
                    onChange={(v) => patch("handoff", { [r.k]: v })}
                  />
                ))}
              </div>
              <label className={styles.fieldLabel}>
                העברה אוטומטית אחרי X הודעות (0 = כבוי)
              </label>
              <input
                className={styles.input}
                style={sx.smallInput}
                type="number"
                min="0"
                value={form.handoff.afterMessages || 0}
                onChange={(e) =>
                  patch("handoff", {
                    afterMessages: Math.max(0, parseInt(e.target.value, 10) || 0),
                  })
                }
              />
            </Card>

            {/* 🛍 sales */}
            <Card
              icon="🛍"
              title="מכירות"
              subtitle='למשל: "להוסיף גם ניקוי אבנית?"'
              hint="שניהם כבויים = היא עונה רק על מה שנשאלה."
            >
              <div style={sx.rowsWrap}>
                <ToggleRow
                  label="המלצות על שירותים נוספים"
                  value={form.sales.recommend}
                  onChange={(v) => patch("sales", { recommend: v })}
                />
                <ToggleRow
                  label="Upsell — הצעת תוספות לשירות שנבחר"
                  value={form.sales.upsell}
                  onChange={(v) => patch("sales", { upsell: v })}
                />
              </div>
            </Card>

            {/* 🎯 goal */}
            <Card
              icon="🎯"
              title="היעד המרכזי"
              subtitle="לאן היא מובילה כל שיחה"
              hint="זו לא הגבלה — היא עדיין תענה על הכול. זו ההעדפה שלה כשיש בחירה."
            >
              <Chips
                options={GOALS}
                value={form.goal}
                onChange={(v) => setForm((f) => ({ ...f, goal: v }))}
              />
            </Card>

            {/* 📚 free knowledge */}
            <Card
              icon="📚"
              title="מאגר ידע חופשי"
              subtitle="הדביקו כאן כל מידע על העסק — המזכירה תשען עליו"
              hint="שעות מיוחדות, מבצעים, מדיניות, סיפור העסק. לשאלות שחוזרות עדיף מאגר השאלות והתשובות שלמעלה — שם התשובה מדויקת יותר."
            >
              <textarea
                className={styles.input}
                rows={7}
                value={form.knowledgeText || ""}
                onChange={(e) =>
                  setForm((f) => ({ ...f, knowledgeText: e.target.value }))
                }
                placeholder="שעות מיוחדות, מבצעים, מדיניות, סיפור העסק — כל דבר שתרצו שהיא תדע"
              />
            </Card>

            <div className={styles.saveRow}>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={save}
                disabled={saving || !usable}
              >
                {saving ? <Spinner /> : "שמירת הגדרות המזכירה"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AiReceptionistSettings;
